import {
  BundleOrderItem,
  BundleProduct,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import { getAnalyticsIsAllowed } from '@storefront/services/DataCollection/optanonConsent';

import {
  bundleOrderItemToFormattedProductAttentive,
  getProductViewItems,
  getSelectedProductItems,
  convertCartInformation,
} from './Attentive.utils';
import {
  ProductView,
  AttentiveCartType,
  PurchaseCartType,
  AttentiveProductItemType,
  CartUpdated,
} from './AttentiveTypes';

import { ProductConfiguration } from '@storefront/types/Product';
declare global {
  interface Window {
    attentive?: {
      analytics?: any;
    };
  }
}

interface ProductViewParams {
  activeSKU: string;
  products: GetSkuProductsQuery['products'];
  selectedOptions: string[];
}

class AttentiveAnalyticsHandler {
  private static instance: AttentiveAnalyticsHandler;
  private productView: ProductView = {} as ProductView;
  private purchase: PurchaseCartType = {} as PurchaseCartType;
  private cartUpdated: CartUpdated = {} as CartUpdated;
  private attentiveExists: boolean = false;
  private analyticsAllowed: boolean = false;

  private constructor() {
    this.checkAttentiveExists();
    this.checkAnalyticsAllowed();
  }

  public static getInstance(): AttentiveAnalyticsHandler {
    if (!AttentiveAnalyticsHandler.instance) {
      AttentiveAnalyticsHandler.instance = new AttentiveAnalyticsHandler();
    }
    return AttentiveAnalyticsHandler.instance;
  }

  public setAddProductsToCart(cartId: string, products: ProductViewParams) {
    if (!this.checkAnalyticsAllowed()) return;
    try {
      const productView = this.getProductViewFromUserSelections(products);
      const cartData: AttentiveCartType = {
        ...productView,
        cart: {
          cartId: cartId,
        },
      };
      window.attentive.analytics.addToCart(cartData);
    } catch (err) {
      console.error('Error in setAddProductsToCart:', err);
    }
  }

  public setProductView({
    activeSKU,
    products,
    selectedOptions,
  }: ProductViewParams) {
    if (!this.checkAnalyticsAllowed()) return;
    const isConditionsToTerminate =
      !activeSKU || !selectedOptions || selectedOptions?.length === 0;

    if (isConditionsToTerminate) return;
    try {
      const convertedProductView = this.getProductViewFromUserSelections({
        activeSKU,
        products,
        selectedOptions,
      });
      if (this.attentiveExists && convertedProductView?.items.length > 0) {
        this.productView = convertedProductView;
        window?.attentive?.analytics?.productView(convertedProductView);
      }
    } catch (err) {
      console.error('Error in setProductView:', err);
    }
  }

  private getProductViewFromUserSelections({
    activeSKU,
    products,
    selectedOptions,
  }: ProductViewParams) {
    const selectedProductItems = getSelectedProductItems(activeSKU, products);
    const itemsSelected = getProductViewItems(
      selectedProductItems,
      selectedOptions
    );
    return { items: [...itemsSelected] } as ProductView;
  }

  public getProductView() {
    return this.productView;
  }

  public setPurchase(items: BundleOrderItem[], orderId: string) {
    if (!this.checkAnalyticsAllowed()) return;
    try {
      const products = items.flatMap((item) =>
        bundleOrderItemToFormattedProductAttentive(item)
      );

      /// convert Order to PurchaseCartType
      const convertedPurchase = {
        items: products.map(
          (product) =>
            ({
              productId: product.productId,
              productVariantId: product.productVariantId,
              name: product.name,
              category: product.category,
              quantity: product.quantity,
              price: {
                value: product.price.value.toString(),
                currency: product.price.currency,
              },
            } as AttentiveProductItemType)
        ),
        order: { orderId: orderId },
      } as PurchaseCartType;
      this.purchase = convertedPurchase;
      if (this.attentiveExists)
        window.attentive.analytics.purchase(convertedPurchase);
    } catch (err) {
      console.error('Error in setPurchase:', err);
    }
  }
  public getPurchase() {
    return this.purchase;
  }

  public isAttentiveAvailable() {
    return this.attentiveExists;
  }

  public checkAttentiveExists() {
    this.attentiveExists =
      typeof window !== 'undefined' &&
      !!window.attentive &&
      !!window.attentive.analytics;
    return this.attentiveExists;
  }
  private checkAnalyticsAllowed() {
    this.analyticsAllowed =
      typeof window !== 'undefined' && getAnalyticsIsAllowed();
    return this.analyticsAllowed;
  }

  public setCartUpdated(
    cartId: string,
    configuration: ProductConfiguration,
    product: BundleProduct
  ) {
    if (!this.checkAnalyticsAllowed() || !product || !cartId) return;

    try {
      this.cartUpdated = {
        items: convertCartInformation(configuration, product),
        cart: {
          cartId: cartId,
          cartCoupon: '',
        },
      };
      window.attentive.analytics.cartUpdated(this.cartUpdated);
    } catch (error) {
      console.error('Error in setCartUpdated:', error);
    }
  }

  public getCartUpdated() {
    return this.cartUpdated;
  }
}

export default AttentiveAnalyticsHandler;
